import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Settings,
  Users,
  Building2,
  Combine,
  Target,
  LayoutGrid,
  Share2,
  History,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

/**
 * Phase 5.9 — Consolidated Administration layout.
 *
 * Replaces the 7 separate sidebar entries with a single `/admin/*` shell
 * that hosts horizontal tabs. Each tab renders the existing page component
 * unchanged (just remounted under a new route).
 *
 * Tab ordering matches the previous sidebar ordering. Permission gates that
 * lived on each sidebar item are preserved here per-tab.
 */
const TABS = [
  {
    key: "usuarios",
    path: "usuarios",
    Icon: Users,
    labelKey: "nav.users",
    permission: null, // gated by isAdmin at the page level
  },
  {
    key: "departamentos",
    path: "departamentos",
    Icon: Building2,
    labelKey: "nav.departments",
    permission: null,
  },
  {
    key: "empleados",
    path: "empleados",
    Icon: Combine,
    labelKey: "nav.employeesCanonical",
    permission: "employees:manage",
  },
  {
    key: "scope",
    path: "scope",
    Icon: Target,
    labelKey: "nav.distributionScope",
    permission: "employees:manage",
  },
  {
    key: "secciones-horario",
    path: "secciones-horario",
    Icon: LayoutGrid,
    labelKey: "nav.scheduleSections",
    permission: "schedule:manage",
  },
  {
    key: "links-publicos",
    path: "links-publicos",
    Icon: Share2,
    labelKey: "nav.share",
    permission: "share:manage",
  },
  {
    key: "historial-cargas",
    path: "historial-cargas",
    Icon: History,
    labelKey: "nav.uploadsHistory",
    permission: null,
  },
];

export default function AdminLayout() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const location = useLocation();

  const allowed = TABS.filter(
    (tab) => !tab.permission || hasPermission(tab.permission)
  );

  // Find active tab key for ARIA + sidebar highlight signals.
  const activeKey =
    allowed.find((tab) => location.pathname.startsWith(`/admin/${tab.path}`))?.key ||
    allowed[0]?.key;

  return (
    <div className="space-y-4" data-testid="admin-layout">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Settings className="h-5 w-5 text-primary" />
        <h1 className="text-2xl font-semibold tracking-tight">{t("nav.admin")}</h1>
      </div>

      {/* Horizontal tabs — scrollable on narrow screens, full-bleed on desktop. */}
      <nav
        role="tablist"
        aria-label="Admin sections"
        data-testid="admin-tabs"
        className="-mx-1 flex overflow-x-auto border-b border-border"
      >
        {allowed.map(({ key, path, Icon, labelKey }) => (
          <NavLink
            key={key}
            to={`/admin/${path}`}
            role="tab"
            aria-selected={activeKey === key}
            data-testid={`admin-tab-${key}`}
            className={({ isActive }) =>
              cn(
                "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-t-md border-b-2 px-3 py-2 text-xs font-medium transition-colors",
                isActive
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
              )
            }
          >
            <Icon className="h-3.5 w-3.5" />
            {t(labelKey)}
          </NavLink>
        ))}
      </nav>

      {/* Active tab content */}
      <div data-testid={`admin-tab-content-${activeKey}`}>
        <Outlet />
      </div>
    </div>
  );
}

// Exported so the Sidebar / App.js can know which tab paths are valid
// without re-declaring them.
export const ADMIN_TAB_PATHS = TABS.map((t) => t.path);
